import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  addWeeks,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Doc } from "../../../../../convex/_generated/dataModel";
import CalendarHeader from "./CalendarHeader";
import CalendarGrid from "./CalendarGrid";
import { AddEventSheet } from "../../pages/calendar/team/AddEventSheet";
import EventDetailsSheet from "./EventDetailsSheet";
import { EditEventSheet } from "./EditEventSheet";

const CalendarApp = () => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Fetch real events from the database
  const rawEvents = useQuery(api.calendar.getEvents, {}) || [];

  // Transform the events to match the expected format for the calendar grid
  const events = rawEvents.map((event: any) => {
    // Parse the date string as local date to avoid timezone issues
    const [year, month, dayOfMonth] = event.startDate.split("-").map(Number);
    const eventDate = new Date(year, month - 1, dayOfMonth); // month is 0-indexed

    return {
      id: event._id,
      _id: event._id, // Include the database ID for edit/delete functionality
      title: event.title,
      date: eventDate,
      time: `${event.startTime} - ${event.endTime}`,
      description: event.description || "",
      eventType: event.eventType,
      serviceLocation: event.serviceLocationId || "",
      location: event.location,
      address: event.address,
      assignedStaff: event.assignedStaff,
      businessContext: event.businessContext,
      // Include additional fields needed for editing
      startDate: event.startDate,
      endDate: event.endDate,
      startTime: event.startTime,
      endTime: event.endTime,
      isAllDay: event.isAllDay,
      businessId: event.businessId,
      serviceLocationId: event.serviceLocationId,
      createdByName: event.createdByName,
    };
  });
  const [view, setView] = useState("month"); // 'month' or 'week'
  const [isAddEventSheetOpen, setIsAddEventSheetOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEventDetailsOpen, setIsEventDetailsOpen] = useState(false);
  const [isEditEventOpen, setIsEditEventOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState(null);

  // Navigation functions
  const nextPeriod = () => {
    if (view === "month") {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const prevPeriod = () => {
    if (view === "month") {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, -1));
    }
  };

  // Get days to display based on current view
  const getDaysToDisplay = () => {
    if (view === "month") {
      const start = startOfMonth(currentDate);
      const end = endOfMonth(currentDate);
      return eachDayOfInterval({ start, end });
    } else {
      const start = startOfWeek(currentDate);
      const end = endOfWeek(currentDate);
      return eachDayOfInterval({ start, end });
    }
  };

  // Handle opening add event sheet
  const handleAddEvent = () => {
    setIsAddEventSheetOpen(true);
  };

  // Handle event click
  const handleEventClick = (event: any) => {
    setSelectedEvent(event);
    setIsEventDetailsOpen(true);
  };

  // Handle edit event
  const handleEditEvent = (event: any) => {
    // Convert the display event back to the database format for editing
    const dbEvent = rawEvents.find((e: any) => e._id === event.id);
    if (dbEvent) {
      setEventToEdit(dbEvent);
      setIsEditEventOpen(true);
    }
  };

  const serviceLocations = [
    { id: "1", name: "Main Office" },
    { id: "2", name: "Downtown Branch" },
    { id: "3", name: "West Side Location" },
    { id: "4", name: "East End Facility" },
    { id: "5", name: "South Campus" },
  ];

  return (
    <Card className="w-full">
      <CardContent className="p-4">
        <CalendarHeader
          currentDate={currentDate}
          view={view}
          onPrev={prevPeriod}
          onNext={nextPeriod}
          onViewChange={setView}
          onAddEvent={handleAddEvent}
        />
        <CalendarGrid
          currentDate={currentDate}
          view={view}
          events={events}
          getDaysToDisplay={getDaysToDisplay}
          onEventClick={handleEventClick}
        />
        <AddEventSheet
          isOpen={isAddEventSheetOpen}
          onClose={() => setIsAddEventSheetOpen(false)}
          onEventAdded={() => {
            setIsAddEventSheetOpen(false);
            // Events will automatically refresh due to the useQuery
          }}
          defaultDate={format(currentDate, "yyyy-MM-dd")}
        />
        <EventDetailsSheet
          open={isEventDetailsOpen}
          onOpenChange={setIsEventDetailsOpen}
          event={selectedEvent}
          serviceLocations={serviceLocations}
          onEditEvent={handleEditEvent}
        />
        <EditEventSheet
          isOpen={isEditEventOpen}
          onClose={() => {
            setIsEditEventOpen(false);
            setEventToEdit(null);
          }}
          event={eventToEdit}
          onEventUpdated={() => {
            setIsEditEventOpen(false);
            setEventToEdit(null);
            // Events will automatically refresh due to the useQuery
          }}
        />
      </CardContent>
    </Card>
  );
};

export default CalendarApp;
